import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { Subscription } from 'src/constants/types';
import { usePlansApi } from 'src/modules/plans/api';
import { Plan } from 'src/modules/plans/types';
import {
  useCreateAdminCompanySubscriptionApi,
  useDeleteAdminCompanySubscriptionApi,
  useGetAdminSubscriptionsApi,
  useUpdateAutoRenewStatusOfCompanySubscriptionApi,
} from '../api';
import { useUserCompaniesService } from '../services';
import { CompanySubscriptionTabProps, CompanySubscriptionTabViewModel } from '../types';

export const useCompanySubscriptionTabController = (
  params: CompanySubscriptionTabProps,
): CompanySubscriptionTabViewModel => {
  const { company, refreshCompany } = params;

  const { getAdminPlans } = usePlansApi();
  const getSubscriptions = useGetAdminSubscriptionsApi();
  const createSubscription = useCreateAdminCompanySubscriptionApi();
  const updateAutoRenew = useUpdateAutoRenewStatusOfCompanySubscriptionApi();
  const { getCompanies } = useUserCompaniesService();
  const deleteSubscription = useDeleteAdminCompanySubscriptionApi();
  const [formStapshot, setFormStapshot] = useState<string>();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([
    {
      id: '',
      startDate: '',
      startType: 'onboarding_complete',
      endDate: '',
      autoRenew: true,
      company: company,
    },
  ]);
  const [loading, setLoading] = useState<boolean>(true);
  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const [plans, setPlans] = useState<Plan[]>();
  const [isAdjustFreeCreditsOpen, setAdjustFreeCreditsOpen] = useState(false);
  const [freeCreditsHistoryRefreshKey, setFreeCreditsHistoryRefreshKey] = useState(0);
  const [changePlanSubscription, setChangePlanSubscription] = useState<Subscription>();

  const validate = () => {
    if (subscriptions?.some(s => (s.startType === 'date' && !s.startDate) || !s.plan)) {
      return false;
    }

    return true;
  };

  const getCompanySubscriptions = () => {
    const params = {
      companyIds: company.id,
      startDateInterval: dayjs(new Date().setHours(0, 0, 0, 0)).toISOString(),
      limit: 10000,
      offset: 0,
    };
    getSubscriptions(params)
      .then(response => {
        const subscriptions: Subscription[] =
          response.data.items && response.data.items.length > 0
            ? response.data.items.reverse()
            : [
                {
                  id: '',
                  startDate: '',
                  startType: 'onboarding_complete',
                  endDate: '',
                  autoRenew: true,
                  company: company,
                },
              ];

        setSubscriptions(subscriptions);
        setFormStapshot(JSON.stringify(subscriptions));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onAddNewSubscription = () => {
    // A saved subscription reports this start type only until account setup completes and it gets
    // real dates, so there is no end date yet for another subscription to start after.
    if (subscriptions.some(s => !!s.id && s.startType === 'onboarding_complete')) {
      openDialog({
        title: 'Waiting for Account Setup',
        content:
          'The current subscription starts when account setup is complete, so a new one cannot be scheduled after it yet. ' +
          'To change the plan, delete the current subscription and add a new one.',
        onConfirm: () => {},
        confirmButtonText: 'OK',
        confirmButtonColor: 'primary',
        cancelButtonVariant: 'hidden',
      });
      return;
    }

    setSubscriptions([
      ...subscriptions,
      {
        id: '',
        startDate: '',
        startType: 'date',
        endDate: '',
        autoRenew: true,
        company: company,
      },
    ]);
  };

  const onChangeSubscriptionAutoRenew = (id: string, autoRenew: boolean) => {
    const modifiedSubscriptions = subscriptions.map(s => {
      return {
        ...s,
        autoRenew: s.id === id ? autoRenew : s.autoRenew,
      };
    });
    setSubscriptions(modifiedSubscriptions);
  };

  const onChangeSubscriptionStartDate = (id: string, date: dayjs.Dayjs) => {
    const modifiedSubscriptions = subscriptions.map(s => {
      return {
        ...s,
        startDate: s.id === id ? date.format('YYYY-MM-DD') : s.startDate,
        startType: s.id === id ? 'date' : s.startType,
      };
    });

    setSubscriptions(modifiedSubscriptions);
  };

  const onChangeSubscriptionStartType = (
    id: string,
    startType: 'date' | 'onboarding_complete',
  ) => {
    const modifiedSubscriptions = subscriptions.map(s => {
      return {
        ...s,
        startType:
          s.id === id ? startType : s.startType,
        startDate: s.id === id && startType === 'onboarding_complete' ? '' : s.startDate,
      };
    });

    setSubscriptions(modifiedSubscriptions);
  };

  const onChangeSubscriptionPlan = (id: string, planId: string) => {
    const newPlan = plans?.find(a => a.id === planId);
    if (newPlan) {
      const modifiedSubscriptions = subscriptions.map(s => {
        return {
          ...s,
          plan: s.id === id ? newPlan : s.plan,
          startDate: s.id === id ? '' : s.startDate,
        };
      });

      setSubscriptions(modifiedSubscriptions);
    }
  };

  const isValidForm = () => {
    if (subscriptions?.some(s => (s.startType === 'date' && !s.startDate) || !s.plan)) {
      return false;
    }

    return true;
  };

  const onDeleteSubscription = (subscription: Subscription) => {
    openDialog({
      title: `Delete Current Subscription`,
      content: 'Are you sure you want to delete this subscription?',
      // The dialog keeps the button text of its previous opening
      confirmButtonText: 'Confirm',
      onConfirm: () => {
        if (subscription.id === '') {
          setSubscriptions(subscriptions.filter(s => s.id));
        } else {
          deleteSubscription(subscription.id).then(() => {
            toast('Subscription is deleted');
            const existedSubscriptions = subscriptions.filter(
              s => s.id !== subscription.id,
            );
            setFormStapshot(JSON.stringify(existedSubscriptions));
            setSubscriptions(existedSubscriptions);
          });
        }
      },
    });
  };

  const onSave = () => {
    const isValid = validate();
    if (isValid) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const promises: any[] = [];
      const newSubscription = subscriptions.find(s => s.id === '' && s.plan && s.plan.id);
      if (newSubscription) {
        subscriptions.forEach(s => {
          if (s.id) {
            promises.push(updateAutoRenew(s.id, false));
          }
        });

        Promise.all(promises).then(() => {
          createSubscription(
            company.id,
            newSubscription.plan!.id,
            newSubscription.startDate,
            newSubscription.autoRenew,
            newSubscription.startType,
          ).then(() => {
            toast('subscription is created');
            getCompanies();
            getCompanySubscriptions();
          });
        });
      } else {
        const savedAutoRenew = new Map(
          (JSON.parse(formStapshot ?? '[]') as Subscription[]).map(s => [s.id, s.autoRenew]),
        );
        const changed = subscriptions.filter(s => s.id && savedAutoRenew.get(s.id) !== s.autoRenew);
        // Switched off first: the backend refuses to turn one on while another still renews.
        const updateAll = (autoRenew: boolean) =>
          Promise.all(changed.filter(s => s.autoRenew === autoRenew).map(s => updateAutoRenew(s.id, autoRenew)));
        updateAll(false)
          .then(() => updateAll(true))
          .then(() => {
            toast('Subscriptions are updated', {
              id: 'subscription',
            });
            setFormStapshot(JSON.stringify(subscriptions));
          })
          .catch(e => {
            reportErrorToUser(e, getApiErrorMessage(e, 'Failed to update subscriptions'));
            getCompanySubscriptions();
          });
      }
    }
  };

  useEffect(() => {
    Promise.all([
      getAdminPlans({
        limit: 10000,
        offset: 0,
        applicationId: company.application.id,
        types: 'public,private',
      }).then(response => {
        if (response.data.items) {
          setPlans(response.data.items);
        }
      }),
      getCompanySubscriptions(),
    ]);
  }, [company.application.id]);

  const onFreeCreditsAdjusted = async () => {
    setFreeCreditsHistoryRefreshKey(key => key + 1);
    await refreshCompany();
  };

  return {
    loading,
    company,
    isAdjustFreeCreditsOpen,
    onOpenAdjustFreeCredits: () => setAdjustFreeCreditsOpen(true),
    onCloseAdjustFreeCredits: () => setAdjustFreeCreditsOpen(false),
    onFreeCreditsAdjusted,
    freeCreditsHistoryRefreshKey,
    changePlanSubscription,
    onOpenChangePlan: setChangePlanSubscription,
    onCloseChangePlan: () => setChangePlanSubscription(undefined),
    onPlanChanged: async () => {
      getCompanies();
      getCompanySubscriptions();
      await refreshCompany();
    },
    subscriptions,
    plans,
    isSaveDisabled: !isValidForm() || formStapshot === JSON.stringify(subscriptions),
    hasUnsavedChanges: formStapshot !== JSON.stringify(subscriptions),
    ConfirmationDialog,
    onAddNewSubscription,
    onChangeSubscriptionPlan,
    onChangeSubscriptionStartType,
    onChangeSubscriptionStartDate,
    onChangeSubscriptionAutoRenew,
    onDeleteSubscription,
    onSave,
  };
};
